import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import {
  type RedeemGiftCardMutationVariables,
  useRedeemGiftCardMutation,
} from './graphql/RedeemGiftCard.generated';

// ────────────────────────────────────────────────────────────────────────────────

export const useRedeemGiftCard = () => {
  const [, redeemGiftCardMutation] = useRedeemGiftCardMutation();

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const addErrorBanner = useCallback(
    (id: string, text: string) => {
      addNoticeBanner({ id, text, palette: 'caution' }, true);
    },
    [addNoticeBanner],
  );

  const addSuccessBanner = useCallback(
    (text: string) => {
      addNoticeBanner({ text, palette: 'success' });
    },
    [addNoticeBanner],
  );

  const redeemGiftCard = useCallback(
    async (variables: RedeemGiftCardMutationVariables) => {
      track('account.gift.redeem_form_submit');

      const { data, error } = await redeemGiftCardMutation(variables);

      const redeemGiftCardData = data?.redeemGiftCard;
      const responseTypename = redeemGiftCardData?.__typename;

      // ─────────────────────────────────────────────────────────────────────

      const hasNetworkError = error !== undefined;
      const isUnableToRedeemGiftCard =
        hasNetworkError || responseTypename === 'UnableToRedeemGiftCardError';
      const isInvalidGiftCard = responseTypename === 'InvalidGiftCardError';
      const isAlreadyRedeemedGiftCard =
        responseTypename === 'GiftCardAssociatedWithAnotherAccountError';
      const hasRedeemedGiftCard = responseTypename === 'GiftCardBalance';
      const isNoBalanceError = responseTypename === 'NoBalanceGiftCardError';

      // ─────────────────────────────────────────────────────────────────────

      if (hasRedeemedGiftCard) {
        track('account.gift.redeem_success');
        addSuccessBanner(formatMessage(messages.submitSuccess));

        return true;
      }

      if (isUnableToRedeemGiftCard) {
        addErrorBanner(
          'unable-to-redeem-gift-card-error-banner',
          formatMessage(messages.submitUnableToRedeemError),
        );

        return false;
      }

      if (isNoBalanceError) {
        addErrorBanner(
          'no-balance-gift-card-error-banner',
          formatMessage(messages.submitNoRemainingBalanceError),
        );

        return false;
      }

      if (isInvalidGiftCard) {
        addErrorBanner(
          'invalid-gift-card-error-banner',
          formatMessage(messages.submitInvalidGiftCardError),
        );

        return false;
      }

      if (isAlreadyRedeemedGiftCard) {
        addErrorBanner(
          'already-applied-gift-card-error-banner',
          formatMessage(messages.submitAlreadyAppliedError),
        );

        return false;
      }

      track('account.gift.redeem_failure', {
        systemError: error,
        userError: responseTypename,
      });

      return false;
    },
    [track, redeemGiftCardMutation, addErrorBanner, formatMessage, addSuccessBanner],
  );

  return { redeemGiftCard };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  submitSuccess: {
    defaultMessage: 'Credit added successfully',
    description: 'Gift Card | Redeem | Submit success',
  },
  submitInvalidGiftCardError: {
    defaultMessage: 'This code or gift card is not valid.',
    description: 'Gift Card | Redeem | Submit invalid gift card error',
  },
  submitAlreadyAppliedError: {
    defaultMessage: 'This code or gift card has already been applied.',
    description: 'Gift Card | Redeem | Submit already applied error',
  },
  submitUnableToRedeemError: {
    defaultMessage: 'Unable to redeem gift card. Please try again later.',
    description: 'Gift Card | Redeem | Submit unable to redeem error',
  },
  submitNoRemainingBalanceError: {
    defaultMessage: 'This gift card has no remaining balance to transfer to your digital account.',
    description: 'Gift Card | Redeem | Submit no remaining balance error',
  },
});
